import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Alert, Button, Card, Col, Dropdown, Grid, message, Modal, Popconfirm, Row, Space, Typography} from 'antd';
import {
    DeleteOutlined,
    DownloadOutlined,
    EllipsisOutlined,
    PlusOutlined,
    ReloadOutlined,
    UploadOutlined
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import AgGrid from './common/AgGrid';
import {
    createDomainData,
    createDomainDataBatch,
    deleteDomainData,
    deleteDomainDataBatch,
    getDomainDataList
} from '../api/domainDataApi';
import ExcelUploadModal from './ExcelUploadModal';
import DomainDataCreateModal from './DomainDataCreateModal';
import SearchComponent from './SearchComponent';
import {AG_GRID_LOCALE_TEXT, AG_GRID_PAGE_SIZE_OPTIONS} from '../config/agGridConfig';
import useApiPermission from '../hooks/useApiPermission';
import PermissionButton from './common/PermissionButton';
import PermissionGate from './common/PermissionGate';

const { Title } = Typography;

function DomainDataList() {
  const [rowData, setRowData] = useState([]);
  const [, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [searchColumn, setSearchColumn] = useState('all');
  const [deleting, setDeleting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isExcelModalOpen, setIsExcelModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const gridRef = useRef();
  const screens = Grid.useBreakpoint();
  const isXs = !screens.md;

  // 권한 훅: 버튼/동작 제어
  const { canCall, loading: permLoading } = useApiPermission();
  const canRead = canCall(getDomainDataList);
  const canCreate = canCall(createDomainData);
  const canUpload = canCall(createDomainDataBatch);
  // 배치 삭제 권한은 단건 삭제 메타(deleteDomainData)로 판별
  const canDeleteBatch = canCall(deleteDomainData);

  const searchOptions = [
    { value: 'all', label: '전체' },
    { value: 'domainNm', label: '도메인명' },
    { value: 'domainGroupNm', label: '도메인그룹명' },
    { value: 'domainClsfNm', label: '도메인분류명' },
    { value: 'domainExpln', label: '도메인설명' },
    { value: 'dataType', label: '데이터유형' },
    { value: 'rgtr', label: '등록자' },
    { value: 'mdfr', label: '수정자' },
  ];

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  const agGridColumnDefs = [
    { headerName: '', field: 'checkbox', headerCheckboxSelection: true, headerCheckboxSelectionFilteredOnly: true, checkboxSelection: true, width: 33, pinned: 'left', lockPosition: true, suppressMenu: true, sortable: false, filter: false, resizable: false, suppressExport: true },
    { headerName: '도메인데이터ID', field: 'domainDataId', hide: true },
    { headerName: '도메인그룹명', field: 'domainGroupNm', width: 100 },
    { headerName: '도메인분류명', field: 'domainClsfNm', width: 100 },
    { headerName: '도메인명', field: 'domainNm', width: 100 },
    { headerName: '도메인설명', field: 'domainExpln', minWidth: 200, flex: 1},
    { headerName: '데이터유형', field: 'dataType', width: 100 },
    { headerName: '데이터길이', field: 'dataLen', cellStyle: { 'text-align': 'center' }, width: 100 },
    { headerName: '소수점길이', field: 'dcptLen', cellStyle: { 'text-align': 'center' }, width: 100 },
    { headerName: '저장형식', field: 'strgFrm', width: 100 },
    { headerName: '표시형식', field: 'indctFrm', width: 100 },
    { headerName: '단위', field: 'unit', cellStyle: { 'text-align': 'center' }, width: 100 },
    { headerName: '허용값', field: 'prmVl', width: 100 },
    { headerName: '등록자', field: 'rgtr', cellStyle: { 'text-align': 'center' }, width: 100 },
    { headerName: '등록일시', field: 'regDt', sort: 'desc', sortable: true, cellStyle: { 'text-align': 'center' }, valueFormatter: (params) => formatDateTime(params.value), width: 120 },
    { headerName: '수정자', field: 'mdfr', cellStyle: { 'text-align': 'center' }, width: 100 },
    { headerName: '수정일시', field: 'mdfcnDt', cellStyle: { 'text-align': 'center' }, valueFormatter: (params) => formatDateTime(params.value), width: 120 },
  ];

  const agGridDefaultColDef = { sortable: true, filter: true, resizable: true };

  const getFilteredData = () => {
    if (!searchText.trim()) return rowData;
    const searchValue = searchText.toLowerCase();
    return rowData.filter(item => {
      const searchBy = (field) => item[field] && item[field].toLowerCase().includes(searchValue);
      if (searchColumn === 'all') {
        return searchOptions.some(opt => opt.value !== 'all' && searchBy(opt.value));
      }
      return searchBy(searchColumn);
    });
  };

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (permLoading) return; // 권한 로딩 중 대기
      if (!canRead) { setError('목록 조회 권한이 없습니다.'); return; }
      const data = await getDomainDataList();
      if (data && Array.isArray(data.data)) {
        setRowData(data.data);
      } else {
        setError('응답 데이터 형식이 올바르지 않습니다.');
      }
    } catch (err) {
      setError('데이터를 가져오는 데 실패했습니다: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [permLoading, canRead]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const deleteSelectedRowsAgGrid = async () => {
    if (permLoading || !canDeleteBatch) { message.warning('삭제 권한이 없습니다.'); return; }
    if (!gridRef.current || !gridRef.current.api) { message.warning('그리드가 아직 초기화되지 않았습니다.'); return; }
    const selectedNodes = gridRef.current.api.getSelectedNodes();
    if (selectedNodes.length === 0) { message.warning('삭제할 항목을 선택해주세요.'); return; }
    const selectedIds = selectedNodes.map(node => node.data.domainDataId).filter(Boolean);
    if (selectedIds.length === 0) { message.warning('삭제할 항목을 선택해주세요.'); return; }
    setDeleting(true);
    try {
      await deleteDomainDataBatch(selectedIds);
      await fetchList();
      gridRef.current.api?.deselectAll();
      message.success(`${selectedIds.length}개 항목이 삭제되었습니다.`);
    } catch (err) { message.error('삭제 중 오류가 발생했습니다: ' + err.message); }
    finally { setDeleting(false); }
  };

  const handleExcelUploadSuccess = () => fetchList();
  const handleCreateSuccess = () => fetchList();

  // XLSX 다운로드: 현재 표시 열/순서/필터 반영, 첫 열에 UUID(domainDataId) 포함
  const handleDownloadExcel = () => {
    if (!gridRef.current || !gridRef.current.api) { message.warning('그리드가 아직 준비되지 않았습니다.'); return; }

    // 표시 중인 컬럼 수집(재정렬/표시 여부 반영). suppressExport/UUID 제외
    const displayed = gridRef.current.columnApi
      ? gridRef.current.columnApi.getAllDisplayedColumns().map(col => {
          const def = col.getColDef();
          return { field: def.field, headerName: def.headerName || def.field, suppressExport: !!def.suppressExport };
        }).filter(c => c.field && !c.suppressExport && c.field !== 'domainDataId')
      : agGridColumnDefs.filter(c => c.field && !c.suppressExport && c.field !== 'domainDataId').map(c => ({ field: c.field, headerName: c.headerName || c.field }));

    const header = ['UUID', ...displayed.map(c => c.headerName)];
    const rowsAoA = [header];

    gridRef.current.api.forEachNodeAfterFilterAndSort((node) => {
      const dataRow = [node.data?.domainDataId ?? ''];
      displayed.forEach(c => {
        let val = node.data?.[c.field];
        if (c.field === 'regDt' || c.field === 'mdfcnDt') val = formatDateTime(val);
        dataRow.push(val ?? '');
      });
      rowsAoA.push(dataRow);
    });

    const ws = XLSX.utils.aoa_to_sheet(rowsAoA);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Domains');
    XLSX.writeFile(wb, 'domain-data.xlsx');
  };

  if (error) return <Alert message="오류" description={error} type="error" showIcon />;

  const confirmDeleteFromMenu = () => {
    if (permLoading || !canDeleteBatch) { message.warning('삭제 권한이 없습니다.'); return; }
    if (!gridRef.current || !gridRef.current.api) { message.warning('그리드가 아직 초기화되지 않았습니다.'); return; }
    const selectedNodes = gridRef.current.api.getSelectedNodes();
    if (selectedNodes.length === 0) { message.warning('삭제할 항목을 선택해주세요.'); return; }
    Modal.confirm({ title: '선택 항목 삭제', content: '선택된 항목들을 삭제하시겠습니까?', okText: '삭제', okButtonProps: { danger: true }, cancelText: '취소', onOk: deleteSelectedRowsAgGrid });
  };

  const actionMenuItems = [
    ...(canCreate ? [{ key: 'create', icon: <PlusOutlined />, label: '신규생성' }] : []),
    ...(canDeleteBatch ? [{ type: 'divider' }, { key: 'delete', icon: <DeleteOutlined />, label: '선택 항목 삭제', danger: true }] : []),
    ...(canUpload ? [{ type: 'divider' }, { key: 'upload', icon: <UploadOutlined />, label: '엑셀 업로드' }] : []),
    { key: 'download', icon: <DownloadOutlined />, label: '엑셀 다운로드' },
    ...(canRead ? [{ type: 'divider' }, { key: 'refresh', icon: <ReloadOutlined />, label: '새로고침' }] : []),
  ];

  const onActionMenuClick = ({ key }) => {
    switch (key) {
      case 'create': (permLoading || !canCreate) ? message.warning('생성 권한이 없습니다.') : setIsCreateModalOpen(true); break;
      case 'delete': confirmDeleteFromMenu(); break;
      case 'upload': (permLoading || !canUpload) ? message.warning('업로드 권한이 없습니다.') : setIsExcelModalOpen(true); break;
      case 'download': handleDownloadExcel(); break;
      case 'refresh': (permLoading || !canRead) ? message.warning('조회 권한이 없습니다.') : fetchList(); break;
      default: break;
    }
  };

  return (
    <PermissionGate require={getDomainDataList}>
      {({allowed}) => (
        <Card style={{ opacity: allowed ? 1 : 0.5, pointerEvents: allowed ? 'auto' : 'none' }}>
          <Title level={2}>도메인 목록</Title>
          {successMessage && <Alert message="성공" description={successMessage} type="success" showIcon closable onClose={() => setSuccessMessage('')} style={{ marginBottom: 16 }} /> }
          <Card style={{ marginBottom: 16 }}>
            <Row gutter={[8, 8]} align="middle">
              <Col xs={24}>
                {isXs ? (
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', width: '100%' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <SearchComponent searchColumn={searchColumn} setSearchColumn={setSearchColumn} searchText={searchText} setSearchText={setSearchText} searchOptions={searchOptions} defaultSearchColumn={'domainGroupNm'} />
                    </div>
                    <div style={{ flexShrink: 0 }}>
                      <Dropdown menu={{ items: actionMenuItems, onClick: onActionMenuClick }} placement="bottomRight" trigger={["click"]}>
                        <Button aria-label="작업 메뉴" icon={<EllipsisOutlined />} />
                      </Dropdown>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', width: '100%' }}>
                    <div style={{ flex: '1 1 300px', minWidth: 0 }}>
                      <SearchComponent searchColumn={searchColumn} setSearchColumn={setSearchColumn} searchText={searchText} setSearchText={setSearchText} searchOptions={searchOptions} defaultSearchColumn={'domainGroupNm'} />
                    </div>
                    <div style={{ flex: '0 0 auto' }}>
                      <Space wrap>
                        <PermissionButton type="primary" icon={<PlusOutlined />} onClick={() => (permLoading || !canCreate) ? message.warning('생성 권한이 없습니다.') : setIsCreateModalOpen(true)} disabled={permLoading || !canCreate} require={createDomainData}>신규생성</PermissionButton>
                        <Popconfirm title="선택 항목 삭제" description="선택된 항목들을 삭제하시겠습니까?" onConfirm={deleteSelectedRowsAgGrid} okText="삭제" cancelText="취소" disabled={permLoading || !canDeleteBatch}>
                          <PermissionButton danger icon={<DeleteOutlined />} disabled={permLoading || !canDeleteBatch || deleting} loading={deleting} require={deleteDomainData}>선택 항목 삭제</PermissionButton>
                        </Popconfirm>
                        <PermissionButton type="primary" icon={<UploadOutlined />} onClick={() => (permLoading || !canUpload) ? message.warning('업로드 권한이 없습니다.') : setIsExcelModalOpen(true)} disabled={permLoading || !canUpload} require={createDomainDataBatch} style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}>엑셀 업로드</PermissionButton>
                        <Button type="primary" icon={<DownloadOutlined />} onClick={handleDownloadExcel} style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }}>엑셀 다운로드</Button>
                        <PermissionButton icon={<ReloadOutlined />} onClick={() => (permLoading || !canRead) ? message.warning('조회 권한이 없습니다.') : fetchList()} disabled={permLoading || !canRead} require={getDomainDataList}>새로고침</PermissionButton>
                      </Space>
                    </div>
                  </div>
                )}
              </Col>
            </Row>
          </Card>
          <div style={{ width: '100%' }}>
            <AgGrid
              ref={gridRef}
              rowData={getFilteredData()}
              columnDefs={agGridColumnDefs}
              defaultColDef={agGridDefaultColDef}
              paginationPageSize={20}
              paginationPageSizeSelector={AG_GRID_PAGE_SIZE_OPTIONS}
              rowSelection="multiple"
              localeText={AG_GRID_LOCALE_TEXT}
            />
          </div>
          <ExcelUploadModal isOpen={isExcelModalOpen} onClose={() => setIsExcelModalOpen(false)} onSuccess={handleExcelUploadSuccess} agGridColumnDefs={agGridColumnDefs} agGridDefaultColDef={agGridDefaultColDef} fieldMapping={['domainGroupNm', 'domainClsfNm', 'domainNm', 'domainExpln', 'dataType', 'dataLen', 'dcptLen', 'strgFrm', 'indctFrm', 'unit', 'prmVl']} uploadApi={createDomainDataBatch} excelGuide={'엑셀 파일의 첫 번째 행은 헤더로 인식되며, 두 번째 행부터 데이터를 읽어옵니다. A열: *도메인그룹명 | B열: *도메인분류명 | C열: *도메인명 | D열: *도메인설명 | E열: *데이터유형 | F열: 데이터길이 | G열: 소수점길이 | H열: 저장형식 | I열: 표시형식 | J열: 단위 | K열: 허용값'} rowKey="domainDataId" />
          <DomainDataCreateModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} onSuccess={handleCreateSuccess} />
        </Card>
      )}
    </PermissionGate>
  );
}

export default DomainDataList;
